import React from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Clock } from "lucide-react";
import EncoreLogo from "@/components/EncoreLogo";
import { SERVICES, PHONE_DISPLAY, PHONE_TEL } from "@/lib/siteContent";
import { LOCATIONS, METROS, STATES } from "@/lib/locations";
import { useFeatureFlags } from "@/lib/featureFlags";
import useSiteContent from "@/lib/useSiteContent";

export default function SiteFooter() {
  const flags = useFeatureFlags();
  const sc = useSiteContent();
  const address = (sc.home || {}).address || "4215 Tudor Ln, Greensboro, NC 27410";
  const hours = (sc.home || {}).hours || "Mon–Fri, 9:00 AM – 6:00 PM ET";
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`Encore Commercial Capital Corporation, ${address}`)}`;
  const helocEnabled = !!flags?.heloc_enabled;
  const areasEnabled = !!flags?.areas_we_serve_enabled;
  return (
    <footer style={{ background: "var(--ink)" }} data-testid="site-footer">
      <div className="px-4 sm:px-6 md:px-10 lg:px-12 py-16 max-w-[1280px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <EncoreLogo monochrome />
          <p className="text-sm mt-4 max-w-sm" style={{ color: "rgba(255,255,255,0.65)" }}>
            "The Alternative Lending Source" — fast, flexible business and commercial real
            estate financing for small and growing businesses.
          </p>
          <a href={PHONE_TEL} className="inline-flex items-center gap-2 mt-5 text-base font-medium text-white" data-testid="footer-phone-link">
            <Phone size={16} strokeWidth={1.5} /> {PHONE_DISPLAY}
          </a>
          <a href={mapsUrl} target="_blank" rel="noreferrer"
             className="flex items-start gap-1.5 text-xs mt-2 transition-colors hover:text-white"
             style={{ color: "rgba(255,255,255,0.45)" }} data-testid="footer-address">
            <MapPin size={13} strokeWidth={1.5} className="mt-[1px] shrink-0" /> {address}
          </a>
          <p className="flex items-start gap-1.5 text-xs mt-1.5" style={{ color: "rgba(255,255,255,0.45)" }} data-testid="footer-hours">
            <Clock size={13} strokeWidth={1.5} className="mt-[1px] shrink-0" /> {hours}
          </p>
        </div>

        <div className="md:col-span-4">
          <p className="text-xs font-mono uppercase tracking-[0.2em] mb-4" style={{ color: "rgba(255,255,255,0.5)" }}>Financing</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`}
                    className="block py-1.5 text-sm transition-colors hover:text-white"
                    style={{ color: "rgba(255,255,255,0.65)" }}
                    data-testid={`footer-${s.slug}`}>
                {s.name}
              </Link>
            ))}
            {helocEnabled && (
              <Link to="/heloc"
                    className="block py-1.5 text-sm transition-colors hover:text-white"
                    style={{ color: "rgba(255,255,255,0.65)" }}
                    data-testid="footer-heloc">
                HELOC (Business Purposes)
              </Link>
            )}
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs font-mono uppercase tracking-[0.2em] mb-4" style={{ color: "rgba(255,255,255,0.5)" }}>Company</p>
          <Link to="/apply" className="block py-1.5 text-sm transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.65)" }} data-testid="footer-apply-link">Apply for financing</Link>
          <Link to="/get-funded" className="block py-1.5 text-sm transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.65)" }} data-testid="footer-get-funded-link">Get funded in 60 seconds</Link>
          <Link to="/track" className="block py-1.5 text-sm transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.65)" }} data-testid="footer-track-link">Track my application</Link>
          <Link to="/contact" className="block py-1.5 text-sm transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.65)" }} data-testid="footer-contact-link">Contact us</Link>
          <Link to="/faq" className="block py-1.5 text-sm transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.65)" }} data-testid="footer-faq-link">FAQs</Link>
          <Link to="/blog" className="block py-1.5 text-sm transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.65)" }} data-testid="footer-blog-link">Blog</Link>
          <Link to="/login" className="block py-1.5 text-sm transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.65)" }} data-testid="footer-login-link">Broker / staff sign in</Link>
          <Link to="/broker-recruit" className="block py-1.5 text-sm transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.65)" }} data-testid="footer-broker-link">Become a referral partner</Link>
        </div>
      </div>
      {areasEnabled && (
      <div className="px-4 sm:px-6 md:px-10 lg:px-12 py-6 max-w-[1280px] mx-auto" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <p className="text-xs font-mono uppercase tracking-[0.2em] mb-3" style={{ color: "rgba(255,255,255,0.5)" }}>Areas we serve</p>
        <div className="flex flex-wrap gap-x-5 gap-y-1.5" data-testid="footer-locations">
          {LOCATIONS.map((l) => (
            <Link key={l.slug} to={`/locations/${l.slug}`}
                  className="text-xs transition-colors hover:text-white"
                  style={{ color: "rgba(255,255,255,0.55)" }}
                  data-testid={`footer-location-${l.slug}`}>
              {l.city}, NC
            </Link>
          ))}
          {METROS.map((m) => (
            <Link key={m.slug} to={`/locations/${m.slug}`}
                  className="text-xs transition-colors hover:text-white"
                  style={{ color: "rgba(255,255,255,0.55)" }}
                  data-testid={`footer-location-${m.slug}`}>
              {m.city}, {m.state}
            </Link>
          ))}
          {STATES.map((s) => (
            <Link key={s.slug} to={`/locations/${s.slug}`}
                  className="text-xs transition-colors hover:text-white"
                  style={{ color: "rgba(255,255,255,0.55)" }}
                  data-testid={`footer-location-${s.slug}`}>
              {s.name}
            </Link>
          ))}
          <Link to="/locations" className="text-xs font-medium transition-colors hover:text-white"
                style={{ color: "rgba(255,255,255,0.75)" }} data-testid="footer-all-locations">
            All locations →
          </Link>
        </div>
      </div>
      )}
      <div className="px-4 py-5 text-center" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mb-3">
          <a href={mapsUrl} target="_blank" rel="noreferrer"
             className="inline-flex items-start gap-1.5 text-xs transition-colors hover:text-white"
             style={{ color: "rgba(255,255,255,0.6)" }} data-testid="footer-business-address">
            <MapPin size={13} strokeWidth={1.5} className="mt-[1px] shrink-0" /> {address}
          </a>
          <span className="inline-flex items-start gap-1.5 text-xs" style={{ color: "rgba(255,255,255,0.6)" }} data-testid="footer-business-hours">
            <Clock size={13} strokeWidth={1.5} className="mt-[1px] shrink-0" /> {hours}
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 mb-3" data-testid="footer-legal-links">
          <Link to="/terms" className="text-xs transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.6)" }} data-testid="footer-terms-link">Terms & Conditions</Link>
          <Link to="/privacy" className="text-xs transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.6)" }} data-testid="footer-privacy-link">Privacy Policy</Link>
          <Link to="/cookies" className="text-xs transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.6)" }} data-testid="footer-cookies-link">Cookie Policy</Link>
          <Link to="/legal" className="text-xs transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.6)" }} data-testid="footer-legal-repo-link">Legal Repository</Link>
        </div>
        <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.4)" }}>
          © {new Date().getFullYear()} Encore Commercial Capital Corporation. All rights reserved. ·
          Financing subject to credit approval. Terms vary by program and applicant profile.
        </p>
      </div>
    </footer>
  );
}
